import { Spin, Tag } from "antd";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import { MapContainer, Marker, Polygon, Popup, TileLayer, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { geometryToLatLngs, polygonToLatLng, toLatLng } from "@/shared/map/geojson";
import { createPlantIcon } from "@/shared/map/icons";
import { usePlantingsGeoJSON } from "../hooks/usePlantings";
import type { PlantingFeature } from "../types";
import { formatDateTime, plantTypeColor, plantTypeLabel } from "../utils/format";

function FitBounds({ positions }: { positions: [number, number][] }) {
	const map = useMap();
	useEffect(() => {
		if (positions.length === 0) {
			return;
		}
		if (positions.length === 1) {
			map.setView(positions[0], 14);
			return;
		}
		map.fitBounds(L.latLngBounds(positions), { padding: [30, 30], maxZoom: 16 });
	}, [map, positions]);
	return null;
}

function PlantingPopup({ feature }: { feature: PlantingFeature }) {
	const { plantName, plantType, quantity, plantedAt, territoryId } = feature.properties;
	return (
		<div className="text-sm min-w-[180px]">
			<p className="font-semibold mb-1">{plantName ?? "O'simlik"}</p>
			<p className="text-ink-soft text-xs mb-1">{plantTypeLabel(plantType)}</p>
			<p className="mb-1">
				Soni: <strong>{quantity ?? "—"}</strong>
			</p>
			<p className="mb-1">
				Ekilgan: <strong>{formatDateTime(plantedAt)}</strong>
			</p>
			{territoryId && (
				<Link to={`/territories?territoryId=${territoryId}`} className="text-info text-xs">
					Hududni ko'rish →
				</Link>
			)}
		</div>
	);
}

interface PlantingsMapProps {
	organizationId: string;
}

export function PlantingsMap({ organizationId }: PlantingsMapProps) {
	const { data, isFetching } = usePlantingsGeoJSON({ organizationId });

	const features = useMemo(() => data?.features ?? [], [data]);

	// Collect every [lat,lng] from all geometry variants for FitBounds
	const allPositions = useMemo(
		() => features.flatMap((f) => geometryToLatLngs(f.geometry)),
		[features]
	);

	const counts = useMemo(() => {
		let daraxt = 0;
		let kochat = 0;
		let gul = 0;
		for (const f of features) {
			if (f.properties.plantType === "DARAXT") {
				daraxt += 1;
			} else if (f.properties.plantType === "KO'CHAT") {
				kochat += 1;
			} else if (f.properties.plantType === "GUL") {
				gul += 1;
			}
		}
		return { daraxt, kochat, gul, total: features.length };
	}, [features]);

	return (
		<div className="space-y-3">
			{/* Stats bar */}
			<div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-neutral-200 rounded-xl px-4 py-2.5">
				<Tag color="green" className="m-0">
					Daraxtlar: {counts.daraxt}
				</Tag>
				<Tag color="cyan" className="m-0">
					Ko'chatlar: {counts.kochat}
				</Tag>
				<Tag color="magenta" className="m-0">
					Gullar: {counts.gul}
				</Tag>
				<Tag color="blue" className="m-0">
					Jami: {counts.total}
				</Tag>
				{isFetching && <Spin size="small" />}
			</div>

			{/* Map */}
			<div className="h-[calc(100vh-220px)] min-h-[400px] w-full rounded-xl overflow-hidden border border-neutral-200 shadow-card">
				<MapContainer center={[41.56, 60.62]} zoom={7} className="h-full w-full" scrollWheelZoom>
					<TileLayer
						attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					/>
					{allPositions.length > 0 && <FitBounds positions={allPositions} />}

					{features.map((feature) => {
						const { id, plantType } = feature.properties;
						const color = plantTypeColor(plantType);
						const g = feature.geometry;

						if (g.type === "Point") {
							return (
								<Marker key={id} position={toLatLng(g.coordinates)} icon={createPlantIcon(color)}>
									<Popup>
										<PlantingPopup feature={feature} />
									</Popup>
								</Marker>
							);
						}

						if (g.type === "MultiPoint") {
							return g.coordinates.map((coord) => (
								<Marker
									key={`${id}-${coord[0]}-${coord[1]}`}
									position={toLatLng(coord)}
									icon={createPlantIcon(color)}
								>
									<Popup>
										<PlantingPopup feature={feature} />
									</Popup>
								</Marker>
							));
						}

						if (g.type === "Polygon") {
							return (
								<Polygon
									key={id}
									positions={polygonToLatLng(g.coordinates)}
									pathOptions={{ color, fillColor: color, fillOpacity: 0.35, weight: 2 }}
								>
									<Popup>
										<PlantingPopup feature={feature} />
									</Popup>
								</Polygon>
							);
						}

						// MultiPolygon
						return g.coordinates.map((poly) => (
							<Polygon
								key={`${id}-${poly[0]?.[0]?.[0]}-${poly[0]?.[0]?.[1]}`}
								positions={polygonToLatLng(poly)}
								pathOptions={{ color, fillColor: color, fillOpacity: 0.35, weight: 2 }}
							>
								<Popup>
									<PlantingPopup feature={feature} />
								</Popup>
							</Polygon>
						));
					})}
				</MapContainer>
			</div>
		</div>
	);
}
